/**
 * Shared HITL settle helpers — restore, SSE, reconcile, and UI gate all use these
 * so no path can re-arm Accept/Approve after a decision is already painted.
 */
import type { BuilderMessage, StructuredIntent } from "./types";

export function composioApprovalIdFromMessage(message: BuilderMessage): string | null {
  if (message.type !== "user") return null;
  const details = message.details as
    { kind?: string; pending?: { approvalId?: string } } | undefined;
  if (details?.kind !== "composio-decision") return null;
  return details.pending?.approvalId?.trim() || null;
}

/** Approval ids that must never re-open Accept/Reject after refresh or catch-up. */
export function settledComposioApprovalIds(messages: BuilderMessage[]): Set<string> {
  const ids = new Set<string>();
  for (const message of messages) {
    const id = composioApprovalIdFromMessage(message);
    if (id) ids.add(id);
  }
  return ids;
}

export function isComposioApprovalSettled(
  messages: BuilderMessage[],
  approvalId: string | null | undefined,
): boolean {
  const id = approvalId?.trim();
  if (!id) return false;
  return settledComposioApprovalIds(messages).has(id);
}

/**
 * Session.intent is SoT for approval. Live disk cache can still hold the
 * pre-approve card (pending-* id, awaiting_confirmation) and win on weight —
 * stamp confirmed so Approve stays disabled after refresh.
 */
export function stampConfirmedIntentCards(
  messages: BuilderMessage[],
  snapshotIntent: StructuredIntent | null | undefined,
): BuilderMessage[] {
  if (!snapshotIntent || snapshotIntent.status !== "confirmed") return messages;
  return messages.map((message) => {
    if (message.type !== "intent") return message;
    const prior = message.details as StructuredIntent | undefined;
    if (!prior || typeof prior !== "object") {
      return { ...message, details: snapshotIntent };
    }
    if (prior.status === "confirmed") return message;
    return {
      ...message,
      details: {
        ...prior,
        status: "confirmed" as const,
        confirmedAt: snapshotIntent.confirmedAt ?? prior.confirmedAt,
      },
    };
  });
}
